import React, { useEffect, useMemo, useState } from 'react';
import { apiFetch } from '../../services/apiClient';
import NumBox from '../../components/NumBox';
import SearchSelect from '../../components/SearchSelect';
import PitMap from './PitMap';
import { haul, money, n, cyPerLf, truckFactor } from './trenchMath';
import { jobLabel } from '../../utils/ui';
import { TRUCK_TYPES } from '../../shopOrderTemplates';

// ── Material & Hauling (Ryan, Oct 27 2026) ─────────────────────────────────────
// "The Trenching module without the equipment": say where (a job, an address or a pin) and what you
// need — material in (sand, base…) and/or spoils hauled off — and it compares every pit / dump from
// Settings → Trenching by the real road drive: material + trucking (haul() — the same math as a bid's
// Trenching window), with the truck and material changeable. "🚚 Request a Truck" makes the usual
// Haul-material shop order with the pick, so the shop sees it.

const card = { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, padding: 16 };
const inp = { border: '1px solid #e5e7eb', borderRadius: 8, padding: '8px 10px', fontSize: 14, boxSizing: 'border-box', fontFamily: 'inherit', background: '#fff' };
const lbl = { fontSize: 11, fontWeight: 800, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.3, display: 'block', marginBottom: 4 };
const navBtn = { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, padding: '7px 12px', fontSize: 13, fontWeight: 600, color: 'var(--text)', cursor: 'pointer' };
const UNITS = [['TON', 'Tons'], ['CY', 'Yards'], ['LOADS', 'Loads']];
const SPOIL_TPC = 1.4;   // tons in a yard of spoils, for a dump that charges by the ton
const r1 = v => Math.round(n(v) * 10) / 10;
const crowMiles = (a, b) => {
  const R = 3958.8, rad = x => x * Math.PI / 180, dLat = rad(b[0] - a[0]), dLng = rad(b[1] - a[1]);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(rad(a[0])) * Math.cos(rad(b[0])) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
};
// "Any sand" / "Any base" match by name; anything else is one material name exactly.
const ANY = { '*sand': /sand/i, '*base': /base/i };
const matches = (want, name) => (ANY[want] ? ANY[want].test(name || '') : String(name || '').trim().toLowerCase() === String(want || '').trim().toLowerCase());
// Quantity → cubic yards: tons through the material's tons-per-yard, loads through the truck's size.
const toCy = (qty, unit, tpc, truck) => {
  const q = n(qty), cap = n(truck && truck.data && truck.data.capacity_cy);
  return unit === 'TON' ? q / (n(tpc) || 1.4) : unit === 'LOADS' ? q * cap : q;
};

export default function MaterialHaul({ onBack }) {
  const [all, setAll] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [err, setErr] = useState('');
  const [where, setWhere] = useState({ jobId: '', address: '', pin: null, label: '' });
  const [finding, setFinding] = useState(false);
  const [showMap, setShowMap] = useState(false);
  const [mat, setMat] = useState({ on: true, want: '*base', qty: '', unit: 'TON', truckId: '', pick: '' });
  const [off, setOff] = useState({ on: false, qty: '', unit: 'CY', truckId: '', pick: '' });
  // 📐 A trench sets the amounts (Ryan, Oct 28 2026): material in = length × width × the backfill depth, off-haul =
  // the trench × the swell (Settings → Trenching) — both can still be typed over. 🔁 Back haul: one truck takes
  // spoils out and brings material back, so no leg is driven empty.
  const [trench, setTrench] = useState({ on: false, lf: '', width: '', depth: '', fill: '' });
  const [backhaul, setBackhaul] = useState(false);
  const [routes, setRoutes] = useState({});   // site id → { miles, minutes, source } | 'loading' | 'none'
  const [when, setWhen] = useState('');
  const [busy, setBusy] = useState(false);
  const [made, setMade] = useState([]);

  useEffect(() => {
    apiFetch('/trenching').then(setAll).catch(e => setErr(e?.error || 'Could not load the pits (Settings → Trenching).'));
    apiFetch('/projects-map').then(r => setJobs(Array.isArray(r) ? r : [])).catch(() => {});
  }, []);
  const settings = (all && all.settings) || {};
  const trucks = useMemo(() => (all ? all.trucks : []), [all]);
  const pits = useMemo(() => (all ? all.pits.filter(p => p.data.type !== 'dump') : []), [all]);
  const dumps = useMemo(() => (all ? all.pits.filter(p => p.data.type === 'dump' || p.data.type === 'both') : []), [all]);
  useEffect(() => {
    if (!trucks.length) return;
    setMat(m => ({ ...m, truckId: m.truckId || trucks[0].id }));
    setOff(o => ({ ...o, truckId: o.truckId || trucks[0].id }));
  }, [trucks]);
  const materialNames = useMemo(() => [...new Set(pits.flatMap(p => (p.data.materials || []).map(m => String(m.name || '').trim()).filter(Boolean)))].sort(), [pits]);

  // Where: a job (its pin, else its address found on the map), an address, or a pin dropped by hand.
  async function geocode(q) {
    const raw = String(q || '').trim(); if (!raw) return null;
    const parts = raw.split(',').map(x => x.trim()).filter(Boolean);
    const tries = [...new Set([raw, raw.replace(/^\d+[A-Za-z]?\s*,?\s*/, ''), parts.length > 1 ? parts.slice(1, 3).join(', ') : ''].filter(Boolean))];
    for (const t of tries) {
      try { const r = await apiFetch(`/geocode?q=${encodeURIComponent(t)}`); if (r && r.length) return [Number(r[0].lat), Number(r[0].lng)]; } catch { /* next */ }
    }
    return null;
  }
  async function pickJob(id) {
    const j = jobs.find(x => x.id === id);
    setWhere({ jobId: id, address: (j && j.address) || '', pin: null, label: j ? jobLabel(j) : '' });
    if (!j) return;
    if (j.lat != null && j.lng != null) { setWhere(w => ({ ...w, pin: [Number(j.lat), Number(j.lng)] })); return; }
    if (j.address) { setFinding(true); const at = await geocode(j.address); setFinding(false); if (at) setWhere(w => ({ ...w, pin: at })); else setErr('That job has no map pin and its address didn’t come up — drop a pin on the map.'); }
    else setErr('That job has no map pin or address — type an address or drop a pin.');
  }
  async function findAddress() {
    setErr(''); setFinding(true);
    const at = await geocode(where.address);
    setFinding(false);
    if (at) setWhere(w => ({ ...w, pin: at, label: w.label || w.address })); else setErr('Couldn’t find that address — try the town, or drop a pin on the map.');
  }
  const pin = where.pin;

  const trenchCyOf = t => n(t.lf) * cyPerLf(t.width, t.depth);
  const fillCyOf = t => n(t.lf) * cyPerLf(t.width, t.fill !== '' && t.fill != null ? Math.min(n(t.fill), n(t.depth)) : t.depth);
  const swell = n(settings.swell || 1.25);
  function setTrenchField(k, v) {
    const t = { ...trench, [k]: v };
    setTrench(t);
    const tc = trenchCyOf(t);
    if (!(tc > 0)) return;
    setMat(m => ({ ...m, on: true, qty: String(r1(fillCyOf(t))), unit: 'CY', pick: '' }));
    setOff(o => ({ ...o, on: true, qty: String(r1(tc * swell)), unit: 'CY', pick: '' }));
  }

  // Road drive from each site to the spot (once per site per spot).
  useEffect(() => { setRoutes({}); }, [pin && pin.join()]);
  const sitesInPlay = useMemo(() => {
    if (!pin) return [];
    const s = [];
    if (mat.on) for (const p of pits) if (p.data.lat != null && (p.data.materials || []).some(m => matches(mat.want, m.name))) s.push(p);
    if (off.on) for (const d of dumps) if (d.data.lat != null) s.push(d);
    return s;
  }, [pin && pin.join(), mat.on, mat.want, off.on, pits, dumps]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    for (const site of sitesInPlay) {
      if (routes[site.id]) continue;
      setRoutes(r => ({ ...r, [site.id]: 'loading' }));
      apiFetch(`/trenching/route?from=${site.data.lat},${site.data.lng}&to=${pin[0]},${pin[1]}`)
        .then(x => setRoutes(r => ({ ...r, [site.id]: x }))).catch(() => setRoutes(r => ({ ...r, [site.id]: 'none' })));
    }
  }, [sitesInPlay]); // eslint-disable-line react-hooks/exhaustive-deps
  const drive = site => {
    const r = routes[site.id];
    if (r && r.minutes != null) return { min: r.minutes, miles: r.miles, road: r.source === 'road' };
    const mi = crowMiles(pin, [site.data.lat, site.data.lng]) * n(settings.road_factor || 1.3);
    return { min: Math.round(mi / n(settings.avg_mph || 35) * 60), miles: r1(mi), road: false, loading: r === 'loading' };
  };

  // Material in: every pit selling it — material + trucking.
  const matTruck = trucks.find(t => t.id === mat.truckId) || null;
  const matRows = !pin || !mat.on || !(n(mat.qty) > 0) ? [] : pits.flatMap(p => (p.data.lat == null ? [] : (p.data.materials || []).filter(m => matches(mat.want, m.name)).map(m => {
    const d = drive(p);
    const cy = toCy(mat.qty, mat.unit, m.tons_per_cy, matTruck);
    const tons = cy * (n(m.tons_per_cy) || 1.4);
    const perTon = String(m.unit || 'TON').toUpperCase() === 'TON';
    const priced = n(m.price) > 0;
    const matCost = (perTon ? tons : cy) * n(m.price);
    const h = haul({ cy, truck: matTruck, oneWayMin: d.min, siteMin: p.data.in_out_min, unloadMin: settings.unload_min });
    return { key: p.id + '|' + m.id, pit: p, m, d, cy, tons, perTon, priced, matCost, h, total: matCost + h.cost };
  }))).sort((a, b) => (a.priced === b.priced ? a.total - b.total : a.priced ? -1 : 1));
  const matBest = matRows.find(r => r.priced);
  const matPick = matRows.find(r => r.key === mat.pick) || matBest || matRows[0] || null;

  // Haul-off: every dump — dump fee + trucking.
  const offTruck = trucks.find(t => t.id === off.truckId) || null;
  const offRows = !pin || !off.on || !(n(off.qty) > 0) ? [] : dumps.filter(x => x.data.lat != null).map(x => {
    const d = drive(x);
    const cy = toCy(off.qty, off.unit, SPOIL_TPC, offTruck);
    const h = haul({ cy, truck: offTruck, oneWayMin: d.min, siteMin: x.data.in_out_min, unloadMin: settings.unload_min });
    const fee = n(x.data.dump_fee), unit = String(x.data.dump_fee_unit || 'LOAD').toUpperCase();
    const feeQty = unit === 'CY' ? cy : unit === 'TON' ? cy * SPOIL_TPC : h.loads;
    return { key: x.id, pit: x, d, cy, h, fee, feeUnit: unit, feeCost: fee * feeQty, total: fee * feeQty + h.cost };
  }).sort((a, b) => a.total - b.total);
  const offPick = offRows.find(r => r.key === off.pick) || offRows[0] || null;

  // 🔁 Back haul: the material truck leaves the job loaded with spoils, dumps, loads material and comes back —
  // job → dump → pit → job — instead of two round trips with an empty leg each. As many trips pair up as
  // the smaller side has loads; the rest are ordinary round trips. Same yard for both = one visit.
  const bhOn = !!(backhaul && mat.on && off.on && matPick && offPick && matTruck);
  const sameYard = bhOn && matPick.pit.id === offPick.pit.id;
  const legKey = bhOn && !sameYard ? `${offPick.pit.id}>${matPick.pit.id}` : null;
  useEffect(() => {
    if (!legKey || routes[legKey]) return;
    const d = offPick.pit.data, q = matPick.pit.data;
    setRoutes(r => ({ ...r, [legKey]: 'loading' }));
    apiFetch(`/trenching/route?from=${d.lat},${d.lng}&to=${q.lat},${q.lng}`)
      .then(x => setRoutes(r => ({ ...r, [legKey]: x }))).catch(() => setRoutes(r => ({ ...r, [legKey]: 'none' })));
  }, [legKey]); // eslint-disable-line react-hooks/exhaustive-deps
  let bh = null;
  if (bhOn) {
    const cap = n(matTruck.data.capacity_cy);
    const outCy = toCy(off.qty, off.unit, SPOIL_TPC, matTruck);
    const inLoads = cap > 0 ? Math.ceil(matPick.cy / cap) : 0, outLoads = cap > 0 ? Math.ceil(outCy / cap) : 0;
    const paired = Math.min(inLoads, outLoads);
    const lr = legKey && routes[legKey];
    const leg = sameYard ? 0 : lr && lr.minutes != null ? lr.minutes
      : Math.round(crowMiles([offPick.pit.data.lat, offPick.pit.data.lng], [matPick.pit.data.lat, matPick.pit.data.lng]) * n(settings.road_factor || 1.3) / n(settings.avg_mph || 35) * 60);
    const handle = n(settings.unload_min);
    const inOut = site => n(site.data.in_out_min);
    // Loading spoils at the job + tipping material there = two handlings at the job per paired trip.
    const f = truckFactor(matTruck);   // the truck's own pace on every drive
    const pairMin = (offPick.d.min + leg + matPick.d.min) * f + inOut(offPick.pit) + (sameYard ? 0 : inOut(matPick.pit)) + 2 * handle;
    const inRound = 2 * matPick.d.min * f + inOut(matPick.pit) + handle;
    const outRound = 2 * offPick.d.min * f + inOut(offPick.pit) + handle;
    const mins = paired * pairMin + (inLoads - paired) * inRound + (outLoads - paired) * outRound;
    const hours = Math.round(mins / 60 * 100) / 100, cost = (mins / 60) * n(matTruck.data.hourly_cost);
    const fee = n(offPick.pit.data.dump_fee), feeUnit = String(offPick.pit.data.dump_fee_unit || 'LOAD').toUpperCase();
    const feeCost = fee * (feeUnit === 'CY' ? outCy : feeUnit === 'TON' ? outCy * SPOIL_TPC : outLoads);
    const separate = matPick.h.cost + offPick.h.cost;
    bh = { inLoads, outLoads, paired, leg, legLoading: lr === 'loading', pairMin, inRound, outRound, hours, cost, feeCost, separate, saved: separate - cost,
      total: (matPick.priced ? matPick.matCost : 0) + feeCost + cost };
  }

  const placeLabel = where.label || where.address || (pin ? `${pin[0].toFixed(5)}, ${pin[1].toFixed(5)}` : '');
  const truckType = t => (t && TRUCK_TYPES.includes(t.name) ? t.name : '');
  const unitWord = u => (u === 'TON' ? 'Tons' : u === 'LOADS' ? 'Loads' : 'Yards');
  // 🚚 Request a Truck: the usual Haul-material shop order, one per pick (material in / haul-off).
  async function requestTruck() {
    const orders = [];
    if (bh) {
      const r = matPick, o = offPick;
      orders.push({ what: `back haul — ${n(off.qty)} ${unitWord(off.unit).toLowerCase()} of spoils out to ${o.pit.name}, ${n(mat.qty)} ${unitWord(mat.unit).toLowerCase()} of ${r.m.name} back from ${r.pit.name}`, details: {
        need: 'haul', haul_type: /sand/i.test(r.m.name) ? 'Sand' : /base/i.test(r.m.name) ? 'Base' : /\bac\b|asphalt/i.test(r.m.name) ? 'AC' : '',
        material: r.m.name, material_custom: true, qty: String(n(mat.qty)), qty_unit: unitWord(mat.unit), source: r.pit.name, truck_type: truckType(matTruck),
        backhaul: true, dump_site: o.pit.name, off_qty: String(n(off.qty)), off_qty_unit: unitWord(off.unit),
        deliver_to: placeLabel, deliver_lat: pin[0], deliver_lng: pin[1], to_label: where.label || where.address || '' },
        notes: `🔁 Back haul with the ${matTruck.name}: spoils out to ${o.pit.name}, ${r.m.name} back from ${r.pit.name}.\n` +
          `${bh.paired} paired trip${bh.paired === 1 ? '' : 's'} × ${Math.round(bh.pairMin)} min (job → ${o.pit.name} ${o.d.min} min${sameYard ? '' : ` → ${r.pit.name} ${bh.leg} min`} → job ${r.d.min} min)` +
          `${bh.inLoads > bh.paired ? `, + ${bh.inLoads - bh.paired} material-only × ${Math.round(bh.inRound)} min` : ''}${bh.outLoads > bh.paired ? `, + ${bh.outLoads - bh.paired} spoils-only × ${Math.round(bh.outRound)} min` : ''} = ${bh.hours} hrs.\n` +
          `${r.priced ? `Material ${money(r.matCost)} + ` : 'No material price on file; '}dump fee ${money(bh.feeCost)} + trucking ${money(bh.cost)} = ${money(bh.total)} (saves ${money(bh.saved)} of trucking against two separate hauls) (Material & Hauling).` });
    }
    if (!bh && mat.on && matPick) {
      const r = matPick;
      orders.push({ what: `${n(mat.qty)} ${unitWord(mat.unit).toLowerCase()} of ${r.m.name} from ${r.pit.name}`, details: {
        need: 'haul', haul_type: /sand/i.test(r.m.name) ? 'Sand' : /base/i.test(r.m.name) ? 'Base' : /\bac\b|asphalt/i.test(r.m.name) ? 'AC' : '',
        material: r.m.name, material_custom: true, qty: String(n(mat.qty)), qty_unit: unitWord(mat.unit), source: r.pit.name, truck_type: truckType(matTruck),
        deliver_to: placeLabel, deliver_lat: pin[0], deliver_lng: pin[1], to_label: where.label || where.address || '' },
        notes: `From ${r.pit.name}${r.pit.data.address ? ' (' + r.pit.data.address + ')' : ''} — ${r.d.miles} mi, ${r.d.min} min each way${r.d.road ? '' : ' (estimated)'}.\n` +
          `${r.h.loads} load${r.h.loads === 1 ? '' : 's'} × ${Math.round(r.h.roundMin)} min with the ${matTruck ? matTruck.name : 'truck'} = ${r.h.hours} hrs.\n` +
          `${r.priced ? `Material ${money(r.matCost)} (${r1(r.perTon ? r.tons : r.cy)} ${r.perTon ? 'TON' : 'CY'} @ ${money(r.m.price)}) + trucking ${money(r.h.cost)} = ${money(r.total)}` : `Trucking ${money(r.h.cost)}; no material price on file`} (Material & Hauling).` });
    }
    if (!bh && off.on && offPick) {
      const r = offPick;
      orders.push({ what: `haul off ${n(off.qty)} ${unitWord(off.unit).toLowerCase()} to ${r.pit.name}`, details: {
        need: 'haul', haul_type: 'Off-haul', material: 'Spoils', qty: String(n(off.qty)), qty_unit: unitWord(off.unit), dump_site: r.pit.name, truck_type: truckType(offTruck),
        deliver_to: placeLabel, deliver_lat: pin[0], deliver_lng: pin[1], to_label: where.label || where.address || '' },
        notes: `To ${r.pit.name}${r.pit.data.address ? ' (' + r.pit.data.address + ')' : ''} — ${r.d.miles} mi, ${r.d.min} min each way${r.d.road ? '' : ' (estimated)'}.\n` +
          `${r.h.loads} load${r.h.loads === 1 ? '' : 's'} × ${Math.round(r.h.roundMin)} min with the ${offTruck ? offTruck.name : 'truck'} = ${r.h.hours} hrs.\n` +
          `Dump fee ${money(r.feeCost)} + trucking ${money(r.h.cost)} = ${money(r.total)} (Material & Hauling).` });
    }
    if (!orders.length) return;
    if (!window.confirm(`Make ${orders.length === 1 ? 'a Request a Truck order' : orders.length + ' Request a Truck orders'} for the shop?\n\n${orders.map(o => '• ' + o.what).join('\n')}${where.jobId ? '' : '\n\n(No job picked — it goes on the board without one.)'}`)) return;
    setBusy(true); setErr('');
    try {
      const out = [];
      for (const o of orders) {
        const so = await apiFetch('/shop-orders', { method: 'POST', body: JSON.stringify({ type: 'request_truck', title: '', project_id: where.jobId || null, priority: 'normal', needed_by: when || null, details: o.details, notes: o.notes }) });
        out.push({ id: so.id, title: so.title || o.what });
      }
      setMade(out);
    } catch (e) { setErr(e?.error || e?.message || 'The shop order didn’t save.'); }
    setBusy(false);
  }

  const noSites = all && !pits.length && !dumps.length;
  const routeNote = d => (d.loading ? 'finding the road…' : `${d.miles} mi · ${d.min} min${d.road ? '' : ' (est.)'}`);
  const th = { padding: '7px 8px', fontSize: 11, fontWeight: 800, color: '#6b7280', textAlign: 'left', whiteSpace: 'nowrap' };
  const td = { padding: '7px 8px', fontSize: 13, borderTop: '1px solid #f3f4f6', whiteSpace: 'nowrap' };
  const truckSel = (val, set) => {
    const t0 = trucks.find(t => t.id === val);
    return (<>
      <select value={val} onChange={e => set(e.target.value)} style={inp}>
        {trucks.map(t => <option key={t.id} value={t.id}>{t.name}{n(t.data.capacity_cy) ? ` · ${n(t.data.capacity_cy)} CY` : ''}{n(t.data.hourly_cost) ? ` · ${money(t.data.hourly_cost)}/hr` : ''}</option>)}
      </select>
      {t0 && truckFactor(t0) !== 1 && <div style={{ fontSize: 11, color: '#6b7280', marginTop: 3 }}>Drives ×{truckFactor(t0)} the car time (Settings → Trenching)</div>}
    </>);
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
        <button onClick={onBack} style={navBtn}>‹ Back</button>
        <div style={{ flex: 1 }}>
          <h1 style={{ fontSize: 22, fontWeight: 800, margin: 0 }}>⛰️ Material &amp; Hauling</h1>
          <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>Where to get sand, base and fill — or dump spoils — by the road drive, with material and trucking priced out. Pits, dumps and trucks live in Settings → Trenching.</div>
        </div>
      </div>
      {err && <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', borderRadius: 8, padding: '8px 12px', fontSize: 13, marginBottom: 12 }}>{err}</div>}
      {!all && !err && <div style={{ color: 'var(--text-muted)', padding: 20 }}>Loading the pits…</div>}
      {noSites && <div style={{ ...card, color: '#92400e' }}>No pits or dumps yet — add them in Settings → Trenching → Material.</div>}

      {all && !noSites && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, maxWidth: 1000 }}>
          {/* Where */}
          <div style={card}>
            <div style={{ fontSize: 15, fontWeight: 800, marginBottom: 10 }}>📍 Where</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 12, alignItems: 'end' }}>
              <div>
                <label style={lbl}>A job</label>
                <SearchSelect value={where.jobId} onChange={pickJob} placeholder="Search jobs…" inputStyle={{ ...inp, width: '100%' }}
                  options={[{ value: '', label: '— No job —' }, ...jobs.map(j => ({ value: j.id, label: jobLabel(j) }))]} />
              </div>
              <div>
                <label style={lbl}>…or an address</label>
                <div style={{ display: 'flex', gap: 6 }}>
                  <input value={where.address} onChange={e => setWhere(w => ({ ...w, address: e.target.value }))} onKeyDown={e => { if (e.key === 'Enter') findAddress(); }}
                    placeholder="Street, town" style={{ ...inp, flex: 1, minWidth: 0 }} />
                  <button type="button" onClick={findAddress} disabled={!where.address.trim() || finding} style={{ ...navBtn, whiteSpace: 'nowrap' }}>{finding ? 'Finding…' : 'Find'}</button>
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10, flexWrap: 'wrap', fontSize: 13 }}>
              <span style={{ color: pin ? '#065f46' : '#9ca3af', fontWeight: 700 }}>{pin ? `📍 ${placeLabel}` : 'No spot yet — pick a job, find an address, or drop a pin.'}</span>
              <button type="button" onClick={() => setShowMap(s => !s)} style={{ background: 'none', border: 'none', color: '#e87722', fontWeight: 800, cursor: 'pointer', fontSize: 12.5 }}>🗺 {showMap ? 'Hide the map' : pin ? 'Move the pin' : 'Drop a pin'}</button>
            </div>
            {showMap && <div style={{ marginTop: 8 }}><PitMap lat={pin && pin[0]} lng={pin && pin[1]} height={300} onChange={(a, b) => setWhere(w => ({ ...w, pin: [a, b], label: w.label || 'Pin on the map' }))} /></div>}
          </div>

          {/* 📐 From a trench */}
          <div style={{ ...card, opacity: trench.on ? 1 : 0.6 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 15, fontWeight: 800, cursor: 'pointer', marginBottom: trench.on ? 10 : 0 }}>
              <input type="checkbox" checked={trench.on} onChange={e => setTrench(t => ({ ...t, on: e.target.checked }))} style={{ width: 18, height: 18 }} /> 📐 Figure it from a trench
            </label>
            {trench.on && (<>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12 }}>
                {[['lf', 'Length (ft)'], ['width', 'Width (in)'], ['depth', 'Depth (in)'], ['fill', 'Backfill depth (in)']].map(([k, l]) => (
                  <div key={k}>
                    <label style={lbl}>{l}</label>
                    <NumBox value={trench[k]} onChange={e => setTrenchField(k, e.target.value)} placeholder={k === 'fill' ? (trench.depth ? String(trench.depth) : 'all of it') : '0'}
                      style={{ ...inp, width: '100%', textAlign: 'right' }} />
                  </div>
                ))}
              </div>
              {trenchCyOf(trench) > 0
                ? <div style={{ marginTop: 10, fontSize: 13, color: '#374151', lineHeight: 1.6 }}>
                    Trench <b>{r1(trenchCyOf(trench))} CY</b> · Material in <b>{r1(fillCyOf(trench))} CY</b>{trench.fill !== '' && n(trench.fill) < n(trench.depth) ? ` (the bottom ${n(trench.fill)}")` : ' (the whole trench)'} · Off-haul <b>{r1(trenchCyOf(trench) * swell)} CY</b> (× {swell} swell)
                    <div style={{ fontSize: 11.5, color: '#9ca3af' }}>These fill the two amounts below — type over either one if it's different.</div>
                  </div>
                : <div style={{ marginTop: 10, fontSize: 12.5, color: '#9ca3af' }}>Length, width and depth — like the Trenching window. Backfill depth is how much of it gets material (blank = all of it).</div>}
            </>)}
          </div>

          {/* Material in */}
          <div style={{ ...card, opacity: mat.on ? 1 : 0.6 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 15, fontWeight: 800, cursor: 'pointer', marginBottom: mat.on ? 10 : 0 }}>
              <input type="checkbox" checked={mat.on} onChange={e => setMat(m => ({ ...m, on: e.target.checked }))} style={{ width: 18, height: 18 }} /> 🟫 Material in
            </label>
            {mat.on && (<>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
                <div>
                  <label style={lbl}>What</label>
                  <select value={mat.want} onChange={e => setMat(m => ({ ...m, want: e.target.value, pick: '' }))} style={{ ...inp, width: '100%' }}>
                    <option value="*base">Any base</option><option value="*sand">Any sand</option>
                    {materialNames.map(x => <option key={x} value={x}>{x}</option>)}
                  </select>
                </div>
                <div>
                  <label style={lbl}>How much</label>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <NumBox value={mat.qty} onChange={e => setMat(m => ({ ...m, qty: e.target.value, pick: '' }))} style={{ ...inp, flex: 1, minWidth: 0, textAlign: 'right' }} />
                    <select value={mat.unit} onChange={e => setMat(m => ({ ...m, unit: e.target.value }))} style={inp}>{UNITS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
                  </div>
                </div>
                <div><label style={lbl}>Truck</label>{truckSel(mat.truckId, v => setMat(m => ({ ...m, truckId: v })))}</div>
              </div>
              <Compare rows={matRows} pick={matPick} best={matBest} onPick={k => setMat(m => ({ ...m, pick: k }))} pin={pin} qty={mat.qty} th={th} td={td} routeNote={routeNote}
                empty={!pin ? 'Pick the spot first.' : !(n(mat.qty) > 0) ? 'Say how much.' : 'No pit on the map sells that yet — add it in Settings → Trenching.'}
                head={['Pit', 'Drive', 'Material', 'Loads', 'Trucking', 'Total']}
                cells={r => [<b>{r.pit.name}</b>, routeNote(r.d), r.priced ? <span>{money(r.matCost)}<div style={{ fontSize: 11, color: '#6b7280' }}>{r.m.name} · {r1(r.perTon ? r.tons : r.cy)} {r.perTon ? 'TON' : 'CY'} @ {money(r.m.price)}</div></span> : <span style={{ color: '#9ca3af' }}>no price yet<div style={{ fontSize: 11 }}>{r.m.name}</div></span>,
                  `${r.h.loads} × ${Math.round(r.h.roundMin)} min`, money(r.h.cost), <b>{r.priced ? money(r.total) : `${money(r.h.cost)} + material`}</b>]} />
            </>)}
          </div>

          {/* Haul-off */}
          <div style={{ ...card, opacity: off.on ? 1 : 0.6 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 15, fontWeight: 800, cursor: 'pointer', marginBottom: off.on ? 10 : 0 }}>
              <input type="checkbox" checked={off.on} onChange={e => setOff(o => ({ ...o, on: e.target.checked }))} style={{ width: 18, height: 18 }} /> 🚛 Haul-off (spoils to a dump)
            </label>
            {off.on && (<>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
                <div>
                  <label style={lbl}>How much</label>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <NumBox value={off.qty} onChange={e => setOff(o => ({ ...o, qty: e.target.value, pick: '' }))} style={{ ...inp, flex: 1, minWidth: 0, textAlign: 'right' }} />
                    <select value={off.unit} onChange={e => setOff(o => ({ ...o, unit: e.target.value }))} style={inp}>{UNITS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
                  </div>
                </div>
                <div><label style={lbl}>Truck</label>{truckSel(off.truckId, v => setOff(o => ({ ...o, truckId: v })))}</div>
              </div>
              <Compare rows={offRows} pick={offPick} best={offRows[0]} onPick={k => setOff(o => ({ ...o, pick: k }))} pin={pin} qty={off.qty} th={th} td={td} routeNote={routeNote}
                empty={!pin ? 'Pick the spot first.' : !(n(off.qty) > 0) ? 'Say how much.' : 'No dump site on the map yet — add one in Settings → Trenching.'}
                head={['Dump', 'Drive', 'Dump fee', 'Loads', 'Trucking', 'Total']}
                cells={r => [<b>{r.pit.name}</b>, routeNote(r.d), r.fee ? <span>{money(r.feeCost)}<div style={{ fontSize: 11, color: '#6b7280' }}>{money(r.fee)} / {r.feeUnit.toLowerCase()}</div></span> : <span style={{ color: '#9ca3af' }}>no fee on file</span>,
                  `${r.h.loads} × ${Math.round(r.h.roundMin)} min`, money(r.h.cost), <b>{money(r.total)}</b>]} />
            </>)}
          </div>

          {/* 🔁 Back haul */}
          {mat.on && off.on && (
            <div style={{ ...card, borderColor: backhaul ? '#93c5fd' : 'var(--border)', background: backhaul ? '#eff6ff' : 'var(--surface)' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 15, fontWeight: 800, cursor: 'pointer' }}>
                <input type="checkbox" checked={backhaul} onChange={e => setBackhaul(e.target.checked)} style={{ width: 18, height: 18 }} /> 🔁 Back haul — the same truck takes spoils out and brings material back
              </label>
              {backhaul && !bh && <div style={{ marginTop: 8, fontSize: 12.5, color: '#9ca3af' }}>Needs a pit and a dump picked above (and both amounts).</div>}
              {bh && (
                <div style={{ marginTop: 10, fontSize: 13, color: '#1e3a8a', lineHeight: 1.6 }}>
                  <div>With the <b>{matTruck.name}</b>: {bh.outLoads} load{bh.outLoads === 1 ? '' : 's'} out, {bh.inLoads} load{bh.inLoads === 1 ? '' : 's'} in → <b>{bh.paired} paired trip{bh.paired === 1 ? '' : 's'}</b> × {Math.round(bh.pairMin)} min
                    {bh.inLoads > bh.paired && <> + {bh.inLoads - bh.paired} material-only × {Math.round(bh.inRound)} min</>}
                    {bh.outLoads > bh.paired && <> + {bh.outLoads - bh.paired} spoils-only × {Math.round(bh.outRound)} min</>} = <b>{bh.hours} hrs</b></div>
                  <div style={{ fontSize: 12, color: '#475569' }}>A paired trip: job → {offPick.pit.name} ({offPick.d.min} min){sameYard ? ' — dump and load in the same yard —' : <> → {matPick.pit.name} ({bh.legLoading ? 'finding the road…' : `${bh.leg} min`})</>} → job ({matPick.d.min} min), plus yard time and loading/tipping at the job.</div>
                  <div style={{ marginTop: 4 }}>Trucking <b>{money(bh.cost)}</b> instead of {money(bh.separate)} as two hauls — <b style={{ color: bh.saved >= 0 ? '#15803d' : '#b91c1c' }}>{bh.saved >= 0 ? 'saves' : 'costs'} {money(Math.abs(bh.saved))}</b></div>
                </div>
              )}
            </div>
          )}

          {/* Order it */}
          {((mat.on && matPick) || (off.on && offPick)) && (
            <div style={{ ...card, borderColor: '#fcd34d', background: '#fffbeb' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: 240, fontSize: 13.5, color: '#78350f', lineHeight: 1.5 }}>
                  {bh ? <div>🔁 <b>Back haul</b>: {n(off.qty)} {unitWord(off.unit).toLowerCase()} out to {offPick.pit.name}, {n(mat.qty)} {unitWord(mat.unit).toLowerCase()} of {matPick.m.name} back from {matPick.pit.name} — {matPick.priced ? money(bh.total) : `${money(bh.cost + bh.feeCost)} + material`}</div> : (<>
                  {mat.on && matPick && <div>🟫 <b>{n(mat.qty)} {unitWord(mat.unit).toLowerCase()} of {matPick.m.name}</b> from {matPick.pit.name} — {matPick.priced ? money(matPick.total) : `${money(matPick.h.cost)} trucking + material`}</div>}
                  {off.on && offPick && <div>🚛 <b>Haul off {n(off.qty)} {unitWord(off.unit).toLowerCase()}</b> to {offPick.pit.name} — {money(offPick.total)}</div>}
                  </>)}
                </div>
                <label style={{ ...lbl, marginBottom: 0, display: 'flex', alignItems: 'center', gap: 6, textTransform: 'none', fontSize: 12.5 }}>Needed
                  <input type="date" value={when} onChange={e => setWhen(e.target.value)} style={{ ...inp, padding: '6px 8px' }} />
                </label>
                <button type="button" onClick={requestTruck} disabled={busy}
                  style={{ padding: '10px 16px', borderRadius: 8, border: 'none', background: '#b45309', color: '#fff', fontSize: 14, fontWeight: 800, cursor: 'pointer', opacity: busy ? 0.6 : 1 }}>
                  {busy ? 'Making it…' : '🚚 Request a Truck'}
                </button>
              </div>
              {made.length > 0 && <div style={{ marginTop: 10, fontSize: 13, color: '#065f46', fontWeight: 700 }}>✓ On the Shop board: {made.map(m => m.title).join(' · ')}</div>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// The comparison table: cheapest priced one marked; click a row to pick it.
function Compare({ rows, pick, best, onPick, head, cells, empty, th, td }) {
  if (!rows.length) return <div style={{ marginTop: 12, fontSize: 13, color: '#9ca3af' }}>{empty}</div>;
  return (
    <div style={{ overflowX: 'auto', marginTop: 12 }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead><tr><th style={th} />{head.map(h => <th key={h} style={{ ...th, textAlign: h === 'Pit' || h === 'Dump' ? 'left' : 'right' }}>{h}</th>)}</tr></thead>
        <tbody>
          {rows.map(r => {
            const on = pick && pick.key === r.key;
            const c = cells(r);
            return (
              <tr key={r.key} onClick={() => onPick(r.key)} style={{ cursor: 'pointer', background: on ? '#fef3c7' : 'transparent' }}>
                <td style={{ ...td, width: 26 }}><input type="radio" checked={!!on} onChange={() => onPick(r.key)} /></td>
                {c.map((x, i) => <td key={i} style={{ ...td, textAlign: i === 0 ? 'left' : 'right' }}>{x}{i === 0 && best && best.key === r.key && <span style={{ marginLeft: 6, fontSize: 11, fontWeight: 800, color: '#15803d' }}>💲 cheapest</span>}</td>)}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
